import { IClassroom } from "../../classrooms/types";
import { IFrameworkResource } from "../../frameworks";
import { ILanguageResource } from "../../languages";
import { ITrainingPhaseResource } from "../../training-phases";
import { IUserResource } from "../../users";
import {
    createApiQueryHook,
    IApiQueryFunction,
    IOrderable,
    IPaginateable,
    mapOrder,
    mapPagination,
} from "../../utils/api";
import { mapRelationalFilter, RelationalFilter } from "../../utils/filters";
import { serialize } from "../../utils/url";
import { IProfessionalSituationList } from "../types";

export interface IProfessionalSituationListQueryParameters
    extends IPaginateable, IOrderable<"createdAt" | "phaseSkillMatchCount"> {
    filters?: {
        frameworks?: RelationalFilter<IFrameworkResource>;
        languages?: RelationalFilter<ILanguageResource>;
        createdBy?: RelationalFilter<IUserResource>;
        createdByConnectedUser?: boolean;
        title?: string | null;
        compatibleClassroom?: RelationalFilter<IClassroom>;
        trainingPhase?: string | ITrainingPhaseResource;
        compatibility?: "ideal" | "partial";
    };
}

export type IProfessionalSituationListQueryResult = IProfessionalSituationList;

const mapTrainingPhaseFilter = (
    trainingPhase: NonNullable<IProfessionalSituationListQueryParameters["filters"]>["trainingPhase"],
) => {
    if (trainingPhase == null) {
        return;
    }

    const trainingPhaseId = typeof trainingPhase === "string" ? trainingPhase : trainingPhase["@id"];
    const uuidMatch = trainingPhaseId.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);

    if (uuidMatch?.[1] != null) {
        return uuidMatch[1];
    }

    return trainingPhaseId.split("/").filter(Boolean).pop();
};

const mapFilters = (filters: IProfessionalSituationListQueryParameters["filters"]) => {
    const {
        frameworks,
        languages,
        createdBy,
        createdByConnectedUser,
        title,
        compatibleClassroom,
        trainingPhase,
        compatibility,
    } = filters ?? {};

    return {
        "language.uuid": mapRelationalFilter(languages),
        "skills.frameworks.uuid": mapRelationalFilter(frameworks),
        "createdBy.uuid": mapRelationalFilter(createdBy),
        createdByConnectedUser,
        title,
        "compatibleClassroom.uuid": mapRelationalFilter(compatibleClassroom),
        "trainingPhase.uuid": mapTrainingPhaseFilter(trainingPhase),
        compatibility,
    };
};

export const professionalSituationListQuery: IApiQueryFunction<IProfessionalSituationListQueryParameters> = ({
    pagination,
    order,
    filters,
}) => {
    return {
        method: "GET",
        url: `/professional_situations${serialize(
            {
                ...mapPagination(pagination),
                ...mapOrder(order),
                ...mapFilters(filters),
            },
            true,
        )}`,
    };
};

export const useProfessionalSituationListQuery = createApiQueryHook<
    IProfessionalSituationListQueryParameters,
    IProfessionalSituationListQueryResult
>(professionalSituationListQuery);
